/* Quick-add chooser on product cards (collection pages).
   One pill row per selectable option (size, Schnitt, …); colour comes
   from the card context (swatch click or ?variant= card-per-colour
   link). Add-to-cart enables once every row has a selection, then
   adds the matching variant and opens the cart drawer. */

if (!window.customElements.get('quick-add-sizes')) {
  class QuickAddSizes extends HTMLElement {
    connectedCallback() {
      // connectedCallback fires again whenever the grid re-attaches the
      // card (sliders, AJAX filtering) — guard against double listeners
      if (this.qaInitialized) return;
      this.qaInitialized = true;
      try {
        this.variants = JSON.parse(this.dataset.variants);
        this.optionNames = JSON.parse(this.dataset.optionNames);
        this.rows = JSON.parse(this.dataset.rows);
      } catch (e) {
        this.hidden = true;
        return;
      }
      this.rowIndexes = this.rows.map((r) => r.i);
      this.block = this.closest('product-block');
      this.submitBtn = this.querySelector('.quick-add-sizes__submit');
      this.selected = {};

      this.addEventListener('click', (evt) => {
        const pill = evt.target.closest('.quick-add-sizes__btn');
        if (pill) {
          evt.preventDefault();
          this.selectValue(pill);
          return;
        }
        if (evt.target.closest('.quick-add-sizes__submit')) {
          evt.preventDefault();
          this.addToCart(this.submitBtn);
        }
      });

      if (this.block) {
        this.block.addEventListener('mouseenter', () => this.refreshAvailability());
      }
      this.refreshAvailability();
    }

    /* Option values pinned by the card context, for options that have
       no pill row of their own. Named option params (explicit swatch
       choice) win over a ?variant=<id> card-per-colour link. */
    cardConstraints() {
      const link = this.block ? this.block.querySelector('.product-link') : null;
      if (!link) return [];
      let params;
      try {
        params = new URL(link.getAttribute('href'), window.location.origin).searchParams;
      } catch (e) {
        return [];
      }

      const named = [];
      for (let i = 0; i < this.optionNames.length; i += 1) {
        if (this.rowIndexes.includes(i)) continue;
        const value = params.get(this.optionNames[i]);
        if (value) named.push({ index: i, value });
      }
      if (named.length) return named;

      const variantId = params.get('variant');
      if (variantId) {
        const variant = this.variants.find((v) => String(v.id) === variantId);
        if (variant) {
          return variant.o
            .map((value, index) => ({ index, value }))
            .filter((c) => !this.rowIndexes.includes(c.index));
        }
      }
      return [];
    }

    /* Variants matching the card constraints plus the given partial
       row selection ({rowIndex: value, …}). */
    candidates(selection) {
      let list = this.variants;
      this.cardConstraints().forEach((constraint) => {
        const filtered = list.filter((v) => v.o[constraint.index] === constraint.value);
        if (filtered.length) list = filtered;
      });
      Object.entries(selection).forEach(([index, value]) => {
        list = list.filter((v) => v.o[index] === value);
      });
      return list;
    }

    matchVariant() {
      if (!this.isComplete()) return null;
      return this.candidates(this.selected).find((v) => v.a === 1) || null;
    }

    isComplete() {
      return this.rowIndexes.every((i) => this.selected[i]);
    }

    selectValue(pill) {
      if (pill.hasAttribute('disabled')) return;
      const row = pill.dataset.row;
      const wasSelected = pill.classList.contains('is-selected');
      this.querySelectorAll(`.quick-add-sizes__btn[data-row="${row}"]`).forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      if (!wasSelected) {
        pill.classList.add('is-selected');
        pill.setAttribute('aria-pressed', 'true');
        this.selected[row] = pill.dataset.value;
      } else {
        delete this.selected[row];
      }
      this.refreshAvailability();
    }

    refreshAvailability() {
      this.querySelectorAll('.quick-add-sizes__btn').forEach((pill) => {
        const row = pill.dataset.row;
        const otherSelection = {};
        Object.entries(this.selected).forEach(([index, value]) => {
          if (index !== row) otherSelection[index] = value;
        });
        otherSelection[row] = pill.dataset.value;
        const available = this.candidates(otherSelection).some((v) => v.a === 1);
        pill.toggleAttribute('disabled', !available);
        if (!available && pill.classList.contains('is-selected')) {
          pill.classList.remove('is-selected');
          pill.setAttribute('aria-pressed', 'false');
          delete this.selected[row];
        }
      });
      this.submitBtn.toggleAttribute('disabled', !this.matchVariant());
    }

    async addToCart(btn) {
      const variant = this.matchVariant();
      if (!variant || btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');

      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', '1');
      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el && el.dataset.sectionId);
      formData.append('sections', sectionsToUpdate.map((el) => el.dataset.sectionId).join(','));
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || response.status);

        if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
          sectionsToUpdate.forEach((el) => {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }

        const drawer = document.querySelector('.js-cart-drawer');
        if (drawer) drawer.open(btn);

        btn.classList.add('is-success');
        setTimeout(() => btn.classList.remove('is-success'), 1200);

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: variant.id }
        }));
      } catch (e) {
        // Fall back to the product page where the full form handles edge cases
        const link = this.block ? this.block.querySelector('.product-link') : null;
        window.location.href = link ? link.href : theme.routes.cart;
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }

  window.customElements.define('quick-add-sizes', QuickAddSizes);
}


/* Bestseller-Variante auf Karten-Links durchsetzen.
   Drittanbieter-Apps (z.B. B4B Variants Products) schreiben die
   Bild-Links der Produktkarten auf die erste Variante um - hier
   wird die im Metafeld hinterlegte Default-Variante wiederhergestellt. */
(function () {
  function enforce(block) {
    var pref = block.dataset.preferredVariant;
    if (!pref) return;
    block.querySelectorAll('a.product-link[href*="variant="]').forEach(function (a) {
      var href = a.getAttribute('href');
      var m = href.match(/([?&])variant=(\d+)/);
      if (m && m[2] !== pref) {
        a.setAttribute('href', href.replace(/([?&])variant=\d+/, '$1variant=' + pref));
      }
    });
  }
  function enforceAll() {
    document.querySelectorAll('product-block[data-preferred-variant]').forEach(enforce);
  }
  function start() {
    enforceAll();
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () { scheduled = false; enforceAll(); }, 100);
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['href'], childList: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
